import React, {useState} from 'react';
import {compose, graphql} from 'react-apollo';
import gql from 'graphql-tag';
import {Redirect} from '@reach/router';
import {useFormState} from 'react-use-form-state';
import uuid from 'uuid/v4';

import {Card, Input, Button, Loading} from 'ui-kit';

import {BenefitForm} from './components';
import * as styles from './NewPlan.module.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'usd',
  style: 'currency',
});
function formatMoney(moneyString) {
  const onlyDigits = moneyString.replace(/[^0-9.]/g, '');
  const parsedNumber = parseFloat(onlyDigits, 10);
  return currencyFormatter.format(isNaN(parsedNumber) ? 0 : parsedNumber);
}

function NewPlan({mutate}) {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  const [benefits, setBenefits] = useState([]);

  const [formState, {text}] = useFormState({
    title: '',
    description: '',
    priceCents: formatMoney('0'),
  });

  if (shouldRedirect) {
    return <Redirect to="/plans" noThrow />;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setLoading(true);

    try {
      const unformattedPriceCents = parseInt(
        formatMoney(formState.values.priceCents).replace(/[^0-9]/g, ''),
      );

      if (isNaN(unformattedPriceCents)) {
        throw new Error(`Could not un-format ${formState.values.priceCents}`);
      }

      const {
        data: {createMembershipPlan},
      } = await mutate({
        variables: {
          input: {
            title: formState.values.title,
            description: formState.values.description,
            priceCents: unformattedPriceCents,
            benefits: benefits.map(benefit => ({
              name: benefit.name,
              quantity: parseInt(benefit.quantity),
              kind: benefit.kind.toUpperCase(),
            })),
          },
        },
      });

      const {errors: mutationErrors} = createMembershipPlan;

      if (mutationErrors.length > 0) {
        setErrors(
          mutationErrors.reduce((allErrors, error) => {
            const {path, message} = error;

            if (allErrors[path] != null) {
              allErrors[path] = `${allErrors[path]}; ${message}`;
            }

            return {
              ...allErrors,
              [error.path]: error.message,
            };
          }, {}),
        );
        setLoading(false);
      } else {
        setLoading(false);
        setErrors({});
        setShouldRedirect(true);
      }
    } catch (error) {
      setErrors({
        global: 'An error occurred. You can try again or come back later.',
      });
      setLoading(false);
    }
  }

  return (
    <Card>
      <h2>New plan</h2>

      <form action="post" onSubmit={handleSubmit}>
        <Input
          label="Plan name"
          errorMessage={errors.title}
          {...text('title')}
        />

        <Input
          label="Yearly price"
          {...text('priceCents')}
          onChange={event =>
            formState.setField('priceCents', event.target.value)
          }
          onBlur={event =>
            formState.setField('priceCents', formatMoney(event.target.value))
          }
          errorMessage={errors.priceCents}
        />

        <Input
          label="Description"
          {...text('description')}
          errorMessage={errors.description}
        />

        <div className={styles.BenefitsContainer}>
          <h4>Trackable benefits</h4>
          {benefits.length === 0 ? (
            <div className={styles.TrackableBenefitsEmpty}>
              <p>
                <small>This plan does not have any trackable benefits.</small>
              </p>
            </div>
          ) : (
            benefits.map(({id, kind, quantity, name}) => {
              return (
                <BenefitForm
                  kind={kind}
                  quantity={quantity}
                  name={name}
                  key={id}
                  onChange={(property, value) => {
                    const benefitToChange = benefits.find(
                      benefit => benefit.id === id,
                    );
                    benefitToChange[property] = value;
                    setBenefits([...benefits]);
                  }}
                  onDelete={() => {
                    const benefitIndex = benefits.findIndex(
                      benefit => benefit.id === id,
                    );
                    setBenefits([
                      ...benefits.slice(0, benefitIndex),
                      ...benefits.slice(benefitIndex + 1),
                    ]);
                  }}
                />
              );
            })
          )}
          <Button
            size="small"
            color="light"
            type="button"
            onClick={() => {
              setBenefits([
                ...benefits,
                {id: uuid(), kind: 'cleaning', name: 'Cleaning', quantity: 2},
              ]);
            }}
          >
            + Add a trackable benefit
          </Button>
        </div>

        <Button type="submit" disabled={loading}>
          {loading ? <Loading immediate /> : 'Create plan'}
        </Button>

        {errors.global == null ? null : (
          <p>
            <small style={{color: 'crimson'}}>{errors.global}</small>
          </p>
        )}
      </form>
    </Card>
  );
}

export default compose(
  graphql(
    gql`
      mutation CreateMembershipPlanMutation(
        $input: CreateMembershipPlanInput!
      ) {
        createMembershipPlan(input: $input) {
          membershipPlan {
            id
            title
            description
            priceCents
          }
          errors {
            path
            message
          }
        }
      }
    `,
  ),
)(NewPlan);
